import { useState } from 'react';
import {
  previewCleanup,
  runCleanup,
  type CleanupResult,
} from '../../services/cleanup';

// Sprint 29.H — Painel admin pro LGPD cleanup ops.
//
// 2 ações:
//   1. "Preview" (dry-run) — mostra counts do que SERIA apagado, sem deletar.
//      Operador valida volume antes de prod cleanup.
//   2. "Run cleanup" — DELETE real. Requer confirmation modal (ação destrutiva).
//
// Retentions defaults aplicados pelo backend:
//   - events: 90 dias
//   - sessions: 365 dias
//   - sanctions revertidas: 5 anos
//   - session_diagnostics: 30 dias
//   - support_diagnostics: 90 dias

interface CountRowProps {
  label: string;
  count: number;
  retention: string;
}

function CountRow({ label, count, retention }: CountRowProps) {
  const isHighlight = count > 0;
  return (
    <tr className="border-b border-[#2a2f36]">
      <td className="py-2 px-3 text-gray-300">{label}</td>
      <td className="py-2 px-3 text-xs text-gray-500">{retention}</td>
      <td
        className={`py-2 px-3 text-right font-mono ${
          isHighlight ? 'text-amber-400' : 'text-gray-500'
        }`}
      >
        {count.toLocaleString()}
      </td>
    </tr>
  );
}

interface ResultDisplayProps {
  result: CleanupResult;
}

function ResultDisplay({ result }: ResultDisplayProps) {
  const total =
    result.events_deleted +
    result.sessions_deleted +
    result.sanctions_deleted +
    result.session_diagnostics_deleted +
    result.support_diagnostics_deleted;

  return (
    <div className="bg-[#161a1f] rounded border border-[#2a2f36]">
      <div className="px-4 py-2 bg-[#0d0f12] border-b border-[#2a2f36] flex justify-between items-center">
        <span className="text-sm text-gray-400">
          Modo:{' '}
          <span className={result.mode === 'preview' ? 'text-blue-400' : 'text-red-400'}>
            {result.mode === 'preview' ? 'PREVIEW (dry-run)' : 'RUN (real DELETE)'}
          </span>
        </span>
        <span className="text-xs text-gray-500">duration {result.duration_ms}ms</span>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs text-gray-500 uppercase">
          <tr>
            <th className="py-2 px-3 text-left">Tabela</th>
            <th className="py-2 px-3 text-left">Retention</th>
            <th className="py-2 px-3 text-right">
              {result.mode === 'preview' ? 'Would delete' : 'Deleted'}
            </th>
          </tr>
        </thead>
        <tbody>
          <CountRow label="events" count={result.events_deleted} retention="90 dias" />
          <CountRow label="sessions" count={result.sessions_deleted} retention="365 dias" />
          <CountRow
            label="sanctions (revertidas)"
            count={result.sanctions_deleted}
            retention="5 anos"
          />
          <CountRow
            label="session_diagnostics"
            count={result.session_diagnostics_deleted}
            retention="30 dias"
          />
          <CountRow
            label="support_diagnostics"
            count={result.support_diagnostics_deleted}
            retention="90 dias"
          />
        </tbody>
        <tfoot className="bg-[#0d0f12] border-t border-[#2a2f36]">
          <tr>
            <td className="py-2 px-3 font-bold text-gray-300" colSpan={2}>
              Total
            </td>
            <td
              className={`py-2 px-3 text-right font-mono font-bold ${
                total > 0 ? 'text-amber-400' : 'text-gray-500'
              }`}
            >
              {total.toLocaleString()}
            </td>
          </tr>
        </tfoot>
      </table>
      {result.error_message && (
        <div className="px-4 py-2 bg-red-900/30 border-t border-red-700 text-red-300 text-sm">
          <span className="font-semibold">Erro:</span> {result.error_message}
        </div>
      )}
    </div>
  );
}

interface ConfirmModalProps {
  open: boolean;
  preview: CleanupResult | null;
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}

function ConfirmModal({ open, preview, onConfirm, onCancel, busy }: ConfirmModalProps) {
  if (!open) return null;
  const total = preview
    ? preview.events_deleted +
      preview.sessions_deleted +
      preview.sanctions_deleted +
      preview.session_diagnostics_deleted +
      preview.support_diagnostics_deleted
    : 0;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40">
      <div className="bg-[#161a1f] border border-red-700 rounded p-6 max-w-md w-full">
        <h2 className="text-lg font-bold text-red-400 mb-2">⚠ Confirmação de DELETE</h2>
        <p className="text-sm text-gray-300 mb-4">
          Você está prestes a apagar permanentemente <strong className="text-amber-400">{total.toLocaleString()}</strong>{' '}
          registros (preview retornou esses counts). Operação <strong>irreversível</strong>.
        </p>
        <p className="text-xs text-gray-500 mb-6">
          Recomendado: rodar Preview primeiro pra confirmar volume. Esse delete é executado pelo
          backend conforme retentions LGPD documentadas em docs/07-PRIVACIDADE-LGPD.md §5.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white transition disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="bg-red-600 hover:bg-red-700 disabled:bg-gray-700 px-4 py-2 rounded text-sm font-medium transition"
          >
            {busy ? 'Apagando…' : 'Confirmar DELETE'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Cleanup() {
  const [preview, setPreview] = useState<CleanupResult | null>(null);
  const [runResult, setRunResult] = useState<CleanupResult | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handlePreview() {
    setError(null);
    setRunResult(null);
    setPreviewBusy(true);
    try {
      const r = await previewCleanup();
      setPreview(r);
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string };
      setError(err?.response?.data?.error?.message || err?.message || 'erro');
    } finally {
      setPreviewBusy(false);
    }
  }

  function openConfirm() {
    if (!preview) {
      setError('Rode Preview primeiro pra ver volume esperado');
      return;
    }
    setError(null);
    setConfirmOpen(true);
  }

  async function handleRun() {
    setRunBusy(true);
    try {
      const r = await runCleanup();
      setRunResult(r);
      setPreview(null); // counts antigos não são mais válidos
      setConfirmOpen(false);
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string };
      setError(err?.response?.data?.error?.message || err?.message || 'erro');
      setConfirmOpen(false);
    } finally {
      setRunBusy(false);
    }
  }

  return (
    <div className="text-gray-200">
      <h1 className="text-2xl font-bold mb-4">Cleanup (LGPD retention)</h1>
      <p className="text-sm text-gray-400 mb-6">
        Aplica retentions LGPD (events 90d / sessions 365d / sanctions revertidas 5y /
        session_diag 30d / support_diag 90d). Preview recomendado antes de DELETE real.
      </p>

      <div className="flex gap-3 mb-6">
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewBusy}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 px-4 py-2 rounded text-sm font-medium transition"
        >
          {previewBusy ? 'Calculando…' : 'Preview (dry-run)'}
        </button>
        <button
          type="button"
          onClick={openConfirm}
          disabled={runBusy || !preview}
          title={!preview ? 'Rode Preview primeiro' : 'Run cleanup com confirmação'}
          className="bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:text-gray-500 px-4 py-2 rounded text-sm font-medium transition"
        >
          Run cleanup (DELETE)
        </button>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded mb-6">
          <span className="font-semibold">Erro:</span> {error}
        </div>
      )}

      {preview && (
        <section className="mb-6">
          <h2 className="text-lg font-bold mb-2 text-blue-400">Preview (dry-run)</h2>
          <ResultDisplay result={preview} />
        </section>
      )}

      {runResult && (
        <section>
          <h2 className="text-lg font-bold mb-2 text-red-400">Resultado do DELETE</h2>
          <ResultDisplay result={runResult} />
        </section>
      )}

      <ConfirmModal
        open={confirmOpen}
        preview={preview}
        onConfirm={handleRun}
        onCancel={() => setConfirmOpen(false)}
        busy={runBusy}
      />
    </div>
  );
}
